/**
  Content of the Terms & Conditions page.

  Shape:
    section = { number, title, clauses: [clause] }
    clause  = { number, heading?, text?, list? }
    list    = { items: [item], inline?, className?, itemClassName? }
    item    = { label, text, list? }

  `inline: false` renders the list as a sibling of the clause instead of inside
  its text. URLs and email addresses inside any `text` are turned into links by
  the page component, so they are written here as plain text.
*/

export const publishedDate = "04 Sept 2026";

const DEFINITIONS = [
  "Lease Agreement — These conditions, the application form, and the price list.",
  "Locker — The safe deposit locker hired by the lessee pursuant to this agreement as identified in the application form.",
  "Lessee — Any natural person or body corporate or other entity identified as the lessee in the application form.",
  "Deposit — A sum of money paid at the start of the term, refundable as set out in these conditions, the amount of which is set out in the application form.",
  "Rent — The rent charged by us for the lease of the locker as set out in this agreement.",
  "Nominee — A person nominated by you to have access to the locker.",
  "Notice — Written communication by either us or you served in accordance with these conditions.",
  "Price List — A list of rent and other charges payable in respect of the locker and additional services (as amended from time to time).",
  "Privacy Policy — Privacy policy of the Company available at https://www.aurm.in/privacy-policy.",
  "Start Date — The start date as stated in the application form.",
  "Term — The initial period of 12 months or such additional period as set out in the application form, commencing on the start date, as extended from time to time in accordance with this agreement.",
  "We / Us / Our / The Company — Pransu Technologies Private Limited.",
  "You / Your — The lessee named in the application form.",
  "Guardian — A parent, legal guardian, or person exercising lawful guardianship over a minor, as recognised under applicable Indian law.",
  "Biometric Data — Fingerprint templates, facial recognition data, and any other biometric identifiers collected from You for the purpose of access verification to the Locker.",
  "DPDP Act — The Digital Personal Data Protection Act, 2023, as amended from time to time, together with any rules, regulations, or guidelines issued thereunder.",
];

export const termsSections = [
  {
    number: "1",
    title: "Definitions",
    clauses: [
      {
        number: "1.1",
        text: "In these Terms and “Conditions”, the following definitions apply.",
        list: {
          inline: false,
          className: "custom-ordered-list text-left",
          itemClassName: "terms d-flex terms_list_text",
          items: DEFINITIONS.map((text, index) => ({
            label: `${String.fromCharCode(97 + index)}.`,
            text,
          })),
        },
      },
      {
        number: "1.2",
        text: "These Conditions, together with the application form, the Privacy Policy (https://www.aurm.in/privacy-policy), and the Price List, form the Lease Agreement under which We agree to provide You the use of the Locker for the Term.",
      },
      {
        number: "1.3",
        text: "These Conditions shall apply to the Lease Agreement and will govern the entire relationship between Us and You throughout the Term.",
      },
      {
        number: "1.4",
        text: "The formation of the Lease Agreement is conditional on You providing Us with such evidence of identity as We may require, our acceptance of the information provided by You in the application form and the payment by You of the Rent and Deposit. You may not open a Locker in the name of or on behalf of any third party.",
      },
      {
        number: "1.5",
        text: "You may only appoint one Nominee at any time, and You may remove such Nominee by Notice to Us signed by the Lessee named in the Application Form.",
      },
      {
        number: "1.6",
        heading: "Eligibility — Age Requirement & Minor Joint Holders",
        list: {
          items: [
            {
              label: "(A)",
              text: "Primary Holder Age Requirement. The primary holder of a Locker must be a natural person who has attained the age of eighteen (18) years as on the Start Date. The Company shall not enter into a Lease Agreement with, or lease a Locker to, any person under the age of eighteen (18) years as the primary holder.",
            },
            {
              label: "(B)",
              text: "Minor as Joint Holder. A person under the age of eighteen (18) years (“Minor”) may be registered as a joint holder of a Locker, subject to the following conditions:",
              list: {
                items: [
                  {
                    label: "a.",
                    text: "The primary holder of the Locker shall be deemed to be the Guardian of the Minor joint holder for all purposes under this Lease Agreement and the Digital Personal Data Protection Act, 2023;",
                  },
                  {
                    label: "b.",
                    text: "The primary holder, acting as Guardian, shall provide verifiable consent in writing, in accordance with Section 9 of the DPDP Act, 2023, before any personal data (including Biometric Data) of the Minor is collected or processed by the Company;",
                  },
                  {
                    label: "c.",
                    text: "The primary holder shall assume full responsibility for all acts, omissions, and obligations of the Minor joint holder arising under or in connection with this Lease Agreement;",
                  },
                  {
                    label: "d.",
                    text: "The primary holder shall provide such proof of the Minor’s identity, age, and the relationship between the primary holder and the Minor as We may reasonably require; and",
                  },
                  {
                    label: "e.",
                    text: "The primary holder acknowledges that withdrawal of consent for the processing of the Minor’s personal data may result in the removal of the Minor as a joint holder.",
                  },
                ],
              },
            },
            {
              label: "(C)",
              text: "Refusal & Remedial Action. We reserve the right to refuse registration of a Minor as a joint holder, or to remove a Minor from a Locker, if We become aware that the requirements of this Section 1.6 have not been satisfied. Any personal data of a Minor collected in violation of this clause shall be promptly deleted in accordance with the DPDP Act and Our Privacy Policy.",
            },
          ],
        },
      },
    ],
  },
  {
    number: "2",
    title: "Rental Deposit & Charges",
    clauses: [
      {
        number: "2.1",
        text: "The Deposit must be paid by You on or before the Start Date. Subject to the provisions of these Conditions, the Deposit is refundable upon termination or expiry of the Lease Agreement, provided You return all debts owed to Us under this Lease Agreement have been paid in full.",
      },
      {
        number: "2.2",
        text: "The Rent for the first twelve (12) months of the Term shall be as set out in the application form. The Rent for the first twelve (12) months of the Term must be paid at the same time as the Deposit in cleared funds.",
      },
      {
        number: "2.3",
        text: "All other charges that may be payable by You at any time, whether in relation to additional services or any other charges which You may from time to time incur, are as set out in Our Price List from time to time. We reserve the right to amend the Price List from time to time and without Notice.",
      },
      {
        number: "2.4",
        text: "If the Term is more than twelve (12) months or is extended for further period of twelve (12) months, the Rent for each additional twelve (12) month period will be the Rent then current in the Price List. A copy of the Price List current from time to time is available from Our office or on Our website. For each twelve (12) month period after the initial twelve (12) month period of the Term, we will invoice You in advance for the charge due for such twelve (12) month period. You must pay such sum or give Us written notice of Your intention to terminate this Lease Agreement with immediate effect within fourteen (14) days of Your receipt of such invoice. Such notice shall be followed by collecting all items remaining in the Locker.",
      },
      {
        number: "2.5",
        text: "All payments may be made by cash, credit, debit, or online transfer. We will not treat any amount as paid until We are in receipt of cleared funds for the total amount owed. We will issue You with an invoice detailing the lease of the Locker and all related services agreed and the amount due to Us.",
      },
      {
        number: "2.6",
        text: "All Our prices and charges are quoted in Indian Rupees, and unless otherwise stated, are exclusive of applicable taxes at the prevailing rate and where applicable.",
      },
    ],
  },
  {
    number: "3",
    title: "Late Payment, Non-Payment, and Interest",
    clauses: [
      {
        number: "3.1",
        text: "Rent and all other charges and/or other payments due under this Lease Agreement are to be paid within the period stated in this Lease Agreement, these Conditions and/ or the Price List (as the case may be) (“Due Date”).",
      },
      {
        number: "3.2",
        text: "If You fail to pay any sums due to Us at any time by the Due Date, interest shall be charged by Us on the outstanding amount at the rate of 18% p.a. from time to time and from the date the payment became due until the outstanding sums are paid in full, both before as well as after any judgment. Our right to interest is without prejudice to any other rights We might have in relation to late payment.",
      },
    ],
  },
  {
    number: "4",
    title: "Suspension of Services",
    clauses: [
      {
        number: "4.1",
        text: "In the event of non-payment by the Due Date for payment of the Rent or any other charges, We reserve the right to suspend the lease of the Locker without further Notice.",
      },
      {
        number: "4.2",
        text: "Pursuant to paragraph above:",
        lead: false,
        list: {
          items: [
            { label: "a.", text: "We will not allow You access to the Locker; and" },
            {
              label: "b.",
              text: "All Our liability to You in respect of the contents of the Locker shall cease, until such time as all outstanding amounts shall have been paid to Us in full together with interest.",
            },
          ],
        },
      },
    ],
  },
  {
    number: "5",
    title: "Release & Disposal",
    clauses: [
      {
        number: "5.1",
        text: "In the event that We have not received payment of all amounts due and owing to Us by the expiry of a period (90) ninety days following the due date on which the first amount outstanding became due under the relevant Lease Agreement, We shall be entitled to, having given You not less than (30) thirty days’ Notice of Our intention to do so, immediately terminate the Lease Agreement (termination date) and We shall be released from all further obligations to You.",
      },
      {
        number: "5.2",
        text: "If You have not cleared Your debt outstanding and due to Us within (60) sixty days of the termination date, we shall be entitled to break open the Locker and keep the contents of the Locker in a sealed cover in the presence of independent witnesses as designated by us, whose signature of an inventory of the content of the locker shall be conclusive evidence.",
      },
      {
        number: "5.3",
        text: "In the event that You or Your Nominee or Your representatives do not claim the contents of the sealed cover within a period of (3) three years from the date of breaking open the Locker, We shall be entitled to sell the contents by way of a public auction and You hereby unconditionally agree and authorize such an auction. We shall be entitled to appropriate net sale proceeds (after meeting expenses for sale) towards Rent and other outstanding dues of locker and expenses incurred by Us in breaking open the Locker.",
      },
    ],
  },
  {
    number: "6",
    title: "Registration of Biometrics & Face ID",
    clauses: [
      {
        number: "6.1",
        text: "When You apply for a Locker with Us, You i.e. the primary account holder and joint holder will be registered with their respective biometrics and face IDs so that the access is restricted to the registered biometrics and face recognitions of the primary holder and joint holder for the respective Locker(s) only.",
      },
      {
        number: "6.2",
        text: "We require You to register the biometrics and face ID which will be used to identify and verify access at the vault door. You will be granted access to the Locker only once the biometric and face recognition access matches the registered biometrics and face IDs.",
      },
    ],
  },
  {
    number: "7",
    title: "Contents of the Locker",
    clauses: [
      {
        number: "7.1",
        text: "You acknowledge that We are not aware of the contents of the Locker and have no knowledge as to the purpose of the storage or the use to which the contents of the Locker may be put at any time.",
      },
      {
        number: "7.2",
        text: "The Locker is designed to store papers, valuables, and other items of a similar nature for lawful purposes only. By entering into this Lease Agreement, You agree that You will not store or allow any other person to store anything that is illegal, offensive, immoral, obscene, indecent, defamatory, slanderous, libelous, noxious, poisonous, corrosive, inflammable, explosive or unstable, nor any guns, knives, firearms, ammunition, chemicals, drugs, plants or plant materials, nor any living organism or any other substance or material which may be the subject of any ban, embargo or import restriction, nor any proceeds of prostitution or drug trafficking or the proceeds of any other crime, nor anything which is otherwise unlawful or which has or may be used in any act of terrorism or which will or may cause any harm whatsoever to any person, premises or place, including (without limitation) the Locker or Our premises, employees, agents, contractors, customers or visitors.",
      },
      {
        number: "7.3",
        text: "You agree to fully indemnify Us (which means You must fully compensate Us for) and pay Us for all costs, charges, expenses, claims or damages that We incur or which are made against Us in the event of any breach by You of this Condition 8.2 in respect of any harm, damage or loss that We or any of Our employees, agents, Lessees, contractors, customers or visitors to Our premises suffer.",
      },
    ],
  },
  {
    number: "8",
    title: "Term, Termination & Refund",
    clauses: [
      {
        number: "8.1",
        text: "This Lease Agreement will commence on the Start Date and will continue and, unless You give Us Notice to renew or extend the Lease Agreement, it shall expire at the end of the Term unless terminated earlier.",
      },
      {
        number: "8.2",
        text: "You may terminate this Agreement at any time by (14) fourteen days prior notice in writing to Us, subject always to collecting all items remaining in the Locker. We will refund to You the Deposit, less any deductions which We are entitled to make in accordance with these Conditions but will be liable to refund the Rent for the unexpired Term.",
      },
      {
        number: "8.3",
        text: "We may agree to an early termination request from You, whereupon we would require You to collect all items remaining in the Locker. You would also be required to confirm that You have removed the contents of the Locker. Any debts owed to Us must be discharged at such time. The Deposit amount would be returned to You within (30) thirty days of Our acceptance of Your termination request, subject to deductions of any amount owed to Us. In such event, we will not be liable to You for refund of the Rent in respect of the Locker for any residual period.",
      },
      {
        number: "8.4",
        text: "We may terminate the Lease Agreement:",
        lead: false,
        list: {
          items: [
            {
              label: "a.",
              text: "With immediate effect, by way of written Notice to You if We become aware of or reasonably suspect any breach of the terms of paragraph 8, or the provisions of any other paragraph(s) within the terms of this agreement.",
            },
            {
              label: "b.",
              text: "In the event of a material breach by You of any of Our Terms which has not been remedied by You within (30) thirty days of Our Notice to You in writing specifying the nature of the breach and the remedy required.",
            },
            {
              label: "c.",
              text: "In the event You are unable to pay Your dues by the Due Date and the Company suspends the lease of the Locker under paragraph 4.",
            },
            {
              label: "d.",
              text: "We may terminate the Lease Agreement immediately if a petition in bankruptcy is presented against You or You are declared bankrupt or similar proceedings are taken in respect of a business or corporate body.",
            },
            { label: "e.", text: "In accordance with Release & Disposal section." },
          ],
        },
      },
      {
        number: "8.5",
        text: "At the end of the Term",
        list: {
          items: [
            {
              label: "a.",
              text: "All outstanding debts due to Us and remaining outstanding, must be paid immediately.",
            },
            { label: "b.", text: "You must immediately remove all contents from the Locker." },
          ],
        },
      },
      {
        number: "8.6",
        text: "If, You do not remove all the contents of the Locker:",
        list: {
          items: [
            {
              label: "a.",
              text: "You shall be liable for and shall pay to Us on demand all relevant charges set out in Our Price List under additional charges, together with all costs and expenses which We may reasonably incur.",
            },
            {
              label: "b.",
              text: "You will also continue to be liable for the applicable Rent in respect of the Locker, current at time of the event, as though the period of hire of the Locker was renewed for a further minimum Lease Agreement Term.",
            },
            {
              label: "c.",
              text: "If the above requirements have not been met by You following a period of (60) sixty days after the end of the Term, we shall be entitled to take all the steps detailed in Release & Disposal Section.",
            },
          ],
        },
      },
    ],
  },
  {
    number: "9",
    title: "Limitation of Liability",
    clauses: [
      {
        number: "9.1",
        text: "While the Company will exercise all such normal precautions as it may in its own discretion deem fit, Company will not be responsible for any loss or deterioration of or damage or loss to articles in locker whether caused by act that is attributable to the fault or negligence from You whatsoever or fire, flood, earthquake, lightening, act of war, civil disorder or any other similar cause(s) beyond the reasonable control of the Company and/ or force majeure events",
      },
      {
        number: "9.2",
        text: "Notwithstanding any other provision of this Lease Agreement, the maximum liability of the Company for any breach or violation is limited to one hundred times the prevailing annual rent of the Locker.",
      },
      {
        number: "9.3",
        text: "Death or mental capacity of Lessee (Personal Account): If You are a private individual and You die or become mentally incapable during the Term, We shall allow Your personal representatives or lawfully appointed attorney to open the Locker, or shall open the Locker for Your personal representatives or attorney provided that such persons produce to Us on demand a valid appointment as attorney, grant of probate or letters of administration appointing such person as the executors or administrators of Your estate, and such persons produce to Us, on demand, a death certificate & evidence of their identity and enter into an indemnity in Our favour in a form satisfactory to Us.",
      },
    ],
  },
  {
    number: "10",
    title: "General",
    clauses: [
      {
        number: "10.1",
        text: "You acknowledge that Your rights regarding the Locker are those of a lessee and nothing in these Conditions grants You any right to ownership in the Locker or the premises in which it is stored or otherwise kept. You must neither assign, sublet nor transfer any interest in the locker or any part of it.",
      },
      {
        number: "10.2",
        text: "If a court or any other competent authority finds that any provision of this Lease Agreement and/or these Conditions (or part of any provision) is invalid, illegal, or unenforceable, that provision or part-provision shall, to the extent required, be deemed deleted, and the validity and enforceability of the other provisions of this Lease Agreement and/or these Conditions shall not be affected.",
      },
      {
        number: "10.3",
        text: "This Agreement is made subject to Indian law and all matters arising out of it shall be subject to the laws of India. (Jurisdiction and dispute resolution are addressed in Section 14.)",
      },
    ],
  },
  {
    number: "11",
    title: "Compliance with the Digital Personal Data Protection Act, 2023",
    clauses: [
      {
        number: "11.1",
        text: "Applicability. The Company is committed to processing all personal data, including Sensitive Personal Data such as Biometric Data, in compliance with the Digital Personal Data Protection Act, 2023 (“DPDP Act”) and any rules or regulations framed thereunder. This Section 11 shall be read in conjunction with the Privacy Policy.",
      },
      {
        number: "11.2",
        text: "Lawful Processing Under Consent. All personal data collected by Us, including but not limited to Your name, contact details, identity documents, and Biometric Data (fingerprints and facial recognition data), shall be processed solely on the basis of Your free, specific, informed, and unambiguous consent. You acknowledge that:",
        list: {
          items: [
            {
              label: "a.",
              text: "Consent shall be obtained through a clear affirmative action, including through the signing of the application form or an electronic consent mechanism;",
            },
            {
              label: "b.",
              text: "The purpose of data collection and processing shall be communicated to You clearly before Your consent is obtained;",
            },
            {
              label: "c.",
              text: "We shall not process Your personal data for any purpose beyond what is necessary for the performance of this Lease Agreement, unless separate consent is obtained.",
            },
          ],
        },
      },
      {
        number: "11.3",
        text: "Right to Withdraw Consent. You have the right to withdraw Your consent for the processing of Your personal data at any time by providing Us with written Notice. Upon receipt of such Notice:",
        list: {
          items: [
            {
              label: "a.",
              text: "We shall cease processing Your personal data to the extent permissible, subject to Our legal and contractual obligations;",
            },
            {
              label: "b.",
              text: "Withdrawal of consent for the processing of Biometric Data that is essential for access to the Locker may result in the inability to provide You continued access to the Locker and may be treated as a request for early termination of this Lease Agreement in accordance with Section 8;",
            },
            {
              label: "c.",
              text: "The withdrawal of consent shall not affect the lawfulness of processing carried out prior to such withdrawal;",
            },
            {
              label: "d.",
              text: "We shall process the withdrawal request within a reasonable period, not exceeding thirty (30) days from receipt of such Notice.",
            },
          ],
        },
      },
      {
        number: "11.4",
        text: "Biometric Data — Collection, Retention, and Deletion.",
        lead: "Biometric Data — Collection, Retention, and Deletion.",
        list: {
          items: [
            {
              label: "a.",
              text: "Purpose Limitation: Biometric Data is collected solely for the purpose of identity verification and secure access control to the Locker. It shall not be used for any other purpose, including profiling, marketing, or surveillance.",
            },
            {
              label: "b.",
              text: "Storage and Security: Biometric Data shall be stored in encrypted form using industry-standard encryption protocols and shall be accessible only to authorised personnel on a need-to-know basis.",
            },
            {
              label: "c.",
              text: "Retention Period: Biometric Data shall be retained for the duration of the Lease Agreement and for a period not exceeding one hundred and eighty (180) days following the expiry or termination of the Lease Agreement, unless a longer retention period is required by applicable law.",
            },
            {
              label: "d.",
              text: "Deletion: Upon expiry of the retention period, or upon receipt of a valid deletion request from You, We shall irrevocably delete or anonymise Your Biometric Data, and shall provide You with written confirmation of such deletion within thirty (30) days.",
            },
            {
              label: "e.",
              text: "No Third-Party Sharing: Biometric Data shall not be shared with, transferred to, or accessed by any third party, except where required by applicable law, court order, or competent government authority.",
            },
          ],
        },
      },
      {
        number: "11.5",
        text: "Sensitive Personal Data. You acknowledge and agree that Biometric Data constitutes sensitive personal data. We shall exercise a higher standard of care in the handling, storage, processing, and protection of such data.",
      },
      {
        number: "11.6",
        text: "Data Principal Rights. In addition to the right to withdraw consent under Section 11.3, You are entitled to exercise the following rights as a Data Principal under the DPDP Act:",
        list: {
          items: [
            {
              label: "a.",
              text: "Right to Access: The right to obtain confirmation of whether Your personal data is being processed and to access a summary of such data;",
            },
            {
              label: "b.",
              text: "Right to Correction and Erasure: The right to request correction of inaccurate personal data or erasure of personal data no longer necessary;",
            },
            {
              label: "c.",
              text: "Right to Grievance Redressal: The right to file a complaint with the Grievance Redressal Officer designated by Us (see Section 13);",
            },
            {
              label: "d.",
              text: "Right to Nominate: The right to nominate any individual to exercise Your rights under the DPDP Act in the event of Your death or incapacity.",
            },
          ],
        },
      },
    ],
  },
  {
    number: "12",
    title: "Intellectual Property Rights & Website Acceptable Use Policy",
    clauses: [
      {
        number: "12.1",
        text: "Ownership of Intellectual Property. All content available on the Company’s website (https://www.aurm.in), including but not limited to text, graphics, logos, icons, images, audio clips, video clips, digital downloads, data compilations, software, and the design, selection, and arrangement thereof, is the exclusive property of Pransu Technologies Private Limited or its content suppliers and is protected by Indian and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.",
      },
      {
        number: "12.2",
        text: "Trademarks. “Aurm”, the Aurm logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Pransu Technologies Private Limited. You must not use such marks without the prior written permission of the Company.",
      },
      {
        number: "12.3",
        text: "Limited Licence. Subject to these Terms, the Company grants You a limited, non-exclusive, non-transferable, revocable licence to access and use the website for Your personal, non-commercial use only. This licence does not include any right to:",
        list: {
          items: [
            {
              label: "a.",
              text: "modify, copy, distribute, transmit, display, perform, reproduce, publish, licence, create derivative works from, transfer, or sell any content obtained from the website;",
            },
            {
              label: "b.",
              text: "use any data mining, robots, scraping, or similar data-gathering or extraction methods;",
            },
            {
              label: "c.",
              text: "download (other than page caching) any portion of the website, except as expressly permitted;",
            },
            {
              label: "d.",
              text: "use the website or its content other than for its intended purpose.",
            },
          ],
        },
      },
      {
        number: "12.4",
        text: "Website Acceptable Use Policy. When using the Company’s website, You agree not to:",
        list: {
          items: [
            {
              label: "a.",
              text: "use the website in any way that violates any applicable law or regulation;",
            },
            {
              label: "b.",
              text: "use the website for the purpose of exploiting, harming, or attempting to exploit or harm minors;",
            },
            {
              label: "c.",
              text: "transmit any advertising, promotional material, “junk mail,” “spam,” or similar solicitation;",
            },
            {
              label: "d.",
              text: "impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person;",
            },
            {
              label: "e.",
              text: "engage in conduct that restricts or inhibits anyone’s use or enjoyment of the website;",
            },
            {
              label: "f.",
              text: "attempt to gain unauthorised access to, interfere with, damage, or disrupt any parts of the website or connected systems;",
            },
            {
              label: "g.",
              text: "introduce any viruses, Trojan horses, worms, logic bombs, or other malicious material;",
            },
            {
              label: "h.",
              text: "use the website in any manner that could disable, overburden, damage, or impair the site;",
            },
            {
              label: "i.",
              text: "reverse engineer, decompile, or disassemble any software used to provide the website or Locker services.",
            },
          ],
        },
      },
      {
        number: "12.5",
        text: "Enforcement. We reserve the right to take any action We deem appropriate in response to violations of this Section 12, including suspending or terminating access, reporting to law enforcement, and pursuing legal remedies.",
      },
    ],
  },
  {
    number: "13",
    title: "Grievance Redressal",
    clauses: [
      {
        number: "13.1",
        text: "In accordance with the Digital Personal Data Protection Act, 2023, and the Information Technology Act, 2000, the Company has appointed a Grievance Redressal Officer to address any grievances, complaints, or concerns relating to Your personal data, including Biometric Data, or any issues arising from these Terms and Conditions or the Privacy Policy.",
      },
      {
        number: "13.2",
        text: "Grievance Redressal Officer Details:",
        lead: "Grievance Redressal Officer Details:",
        list: {
          items: [
            { label: "a.", text: "Name: [NAME]" },
            { label: "b.", text: "Designation: Grievance Redressal Officer" },
            { label: "c.", text: "Address: [ADDRESS]" },
            { label: "d.", text: "Phone: [PHONE]" },
            { label: "e.", text: "Email: grievance@aurm.in" },
          ],
        },
      },
      {
        number: "13.3",
        text: "The Grievance Redressal Officer shall acknowledge receipt of Your grievance within forty-eight (48) hours and shall endeavour to resolve the grievance within thirty (30) days from the date of receipt.",
      },
      {
        number: "13.4",
        text: "If You are not satisfied with the resolution provided by the Grievance Redressal Officer, You may escalate the matter in accordance with the dispute resolution mechanism set out in Section 14.",
      },
    ],
  },
  {
    number: "14",
    title: "Dispute Resolution",
    clauses: [
      {
        number: "14.1",
        text: "Governing Law. This Agreement and any dispute or claim arising out of or in connection with it shall be governed by and construed in accordance with the laws of India.",
      },
      {
        number: "14.2",
        text: "Amicable Resolution. In the event of any dispute, the parties shall first attempt to resolve the Dispute amicably through good-faith negotiation within a period of thirty (30) days from the date on which one party notifies the other in writing.",
      },
      {
        number: "14.3",
        text: "Arbitration. If the Dispute is not resolved amicably within the period specified in Section 14.2, the Dispute shall be referred to and finally resolved by arbitration in accordance with the Arbitration and Conciliation Act, 1996:",
        list: {
          items: [
            { label: "a.", text: "Seat of Arbitration: Bengaluru, Karnataka, India." },
            { label: "b.", text: "Language: English." },
            {
              label: "c.",
              text: "Number of Arbitrators: Sole arbitrator mutually appointed by the parties. If the parties fail to agree within fifteen (15) days, the arbitrator shall be appointed per the Arbitration and Conciliation Act, 1996.",
            },
            {
              label: "d.",
              text: "Award: The arbitral award shall be final and binding on both parties. Costs as determined by the arbitrator.",
            },
            {
              label: "e.",
              text: "Interim Relief: Nothing in this Section 14 shall prevent either party from seeking urgent interim or injunctive relief from any court of competent jurisdiction pending the constitution of the arbitral tribunal.",
            },
          ],
        },
      },
      {
        number: "14.4",
        text: "Jurisdiction. Subject to Section 14.3, the courts at Bengaluru, Karnataka, India shall have exclusive jurisdiction over any proceedings arising out of or in connection with this Lease Agreement.",
      },
    ],
  },
];

export default termsSections;
